/**
 * IDF Code Block Component
 *
 * Renders EnergyPlus IDF code with syntax highlighting that matches
 * the Monaco editor theme used in /editor.
 */

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface IDFCodeBlockProps {
  code: string;
  className?: string;
}

// Token types for IDF syntax
type TokenType =
  | 'comment'
  | 'object-type'
  | 'field-value'
  | 'number'
  | 'delimiter'
  | 'semicolon'
  | 'string'
  | 'default';

interface Token {
  type: TokenType;
  value: string;
}

// CSS classes for each token type - matches Monaco theme colors
const tokenClasses: Record<TokenType, string> = {
  comment: 'text-[#6A9955] dark:text-[#6A9955]', // Green
  'object-type': 'text-[#267F99] dark:text-[#4EC9B0] font-semibold', // Cyan/teal
  'field-value': 'text-[#A31515] dark:text-[#CE9178]', // String color (orange-ish)
  number: 'text-[#098658] dark:text-[#B5CEA8]', // Green numbers
  delimiter: 'text-[#000000] dark:text-[#D4D4D4]', // Default
  semicolon: 'text-[#000000] dark:text-[#D4D4D4] font-bold', // Bold delimiter
  string: 'text-[#A31515] dark:text-[#CE9178]', // String color
  default: 'text-[#1a202c] dark:text-[#e2e8f0]', // Default text
};

/**
 * Tokenize a single line of IDF code
 */
function tokenizeLine(line: string): Token[] {
  const tokens: Token[] = [];

  // Check if line is a comment
  const trimmed = line.trim();
  if (trimmed.startsWith('!')) {
    tokens.push({ type: 'comment', value: line });
    return tokens;
  }

  // Split by comment if present
  const commentIndex = line.indexOf('!');
  const codePart = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  const commentPart = commentIndex >= 0 ? line.slice(commentIndex) : '';

  // Check if this is an object type line (contains comma or semicolon at end, and is first field)
  // Object types are typically the first field and contain colons like "Zone" or "Building"
  let inFirstField = true;
  let currentToken = '';

  for (let i = 0; i < codePart.length; i++) {
    const char = codePart[i];

    if (char === ',') {
      // Push the current token
      if (currentToken.trim()) {
        const trimmedToken = currentToken.trim();
        if (inFirstField && /^[A-Z][A-Za-z0-9:]*$/.test(trimmedToken)) {
          // Object type (e.g., "Zone", "Building", "Coil:Cooling:Water")
          if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
            tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
          }
          tokens.push({ type: 'object-type', value: trimmedToken });
        } else if (/^-?\d+\.?\d*([eE][+-]?\d+)?$/.test(trimmedToken)) {
          // Number
          if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
            tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
          }
          tokens.push({ type: 'number', value: trimmedToken });
        } else {
          // Field value or string
          if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
            tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
          }
          tokens.push({ type: 'field-value', value: trimmedToken });
        }
      } else if (currentToken) {
        tokens.push({ type: 'default', value: currentToken });
      }
      tokens.push({ type: 'delimiter', value: ',' });
      currentToken = '';
      inFirstField = false;
    } else if (char === ';') {
      // Push the current token
      if (currentToken.trim()) {
        const trimmedToken = currentToken.trim();
        if (/^-?\d+\.?\d*([eE][+-]?\d+)?$/.test(trimmedToken)) {
          if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
            tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
          }
          tokens.push({ type: 'number', value: trimmedToken });
        } else {
          if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
            tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
          }
          tokens.push({ type: 'field-value', value: trimmedToken });
        }
      } else if (currentToken) {
        tokens.push({ type: 'default', value: currentToken });
      }
      tokens.push({ type: 'semicolon', value: ';' });
      currentToken = '';
    } else {
      currentToken += char;
    }
  }

  // Push any remaining token
  if (currentToken) {
    const trimmedToken = currentToken.trim();
    if (trimmedToken) {
      if (/^-?\d+\.?\d*([eE][+-]?\d+)?$/.test(trimmedToken)) {
        if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
          tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
        }
        tokens.push({ type: 'number', value: trimmedToken });
      } else if (inFirstField && /^[A-Z][A-Za-z0-9:]*$/.test(trimmedToken)) {
        if (currentToken.startsWith(' ') || currentToken.startsWith('\t')) {
          tokens.push({ type: 'default', value: currentToken.slice(0, currentToken.indexOf(trimmedToken)) });
        }
        tokens.push({ type: 'object-type', value: trimmedToken });
      } else {
        tokens.push({ type: 'default', value: currentToken });
      }
    } else {
      tokens.push({ type: 'default', value: currentToken });
    }
  }

  // Add comment if present
  if (commentPart) {
    tokens.push({ type: 'comment', value: commentPart });
  }

  return tokens;
}

/**
 * Render tokens to React elements
 */
function renderTokens(tokens: Token[], lineIndex: number): React.ReactNode {
  return tokens.map((token, i) => (
    <span key={`${lineIndex}-${i}`} className={tokenClasses[token.type]}>
      {token.value}
    </span>
  ));
}

/**
 * IDF Code Block with syntax highlighting
 */
export function IDFCodeBlock({ code, className }: IDFCodeBlockProps) {
  const highlightedLines = useMemo(() => {
    const lines = code.split('\n');
    return lines.map((line, i) => {
      const tokens = tokenizeLine(line);
      return (
        <div key={i} className="leading-relaxed">
          {renderTokens(tokens, i)}
          {i < lines.length - 1 ? '\n' : ''}
        </div>
      );
    });
  }, [code]);

  return (
    <div className={cn('relative group', className)}>
      <span className="absolute top-2 right-2 text-xs text-muted-foreground bg-background/80 px-1.5 py-0.5 rounded">
        IDF
      </span>
      <pre
        className={cn(
          'bg-[#ffffff] dark:bg-[#0a0f1a]',
          'border border-border rounded-md p-4 overflow-x-auto',
          'font-mono text-sm'
        )}
      >
        <code>{highlightedLines}</code>
      </pre>
    </div>
  );
}

/**
 * Check if content looks like IDF code
 */
export function looksLikeIDF(content: string): boolean {
  const trimmed = content.trim();
  // Check for IDF patterns:
  // - Lines ending with comma or semicolon
  // - Comments starting with !
  // - Object names with colons (e.g., Coil:Cooling:Water)
  const hasIDFComment = /![-\s]/.test(trimmed);
  const hasIDFDelimiter = /[,;]\s*(!|$)/m.test(trimmed);
  const hasObjectName = /^[A-Z][A-Za-z0-9:]+\s*,/m.test(trimmed);

  return hasIDFComment || (hasIDFDelimiter && hasObjectName);
}
